import { DateTime } from 'luxon';
import {
	DateFieldConfiguration,
	DateOffsetConfiguration,
} from '../../../api/ConfigApi';
import { dayjs } from '../../../date-util';
import {
	DATE_PRECISION,
	DATE_PRECISION_BACKEND_FORMAT,
	DATE_PRECISION_INPUT_FORMAT,
	DATE_PRECISION_LUXON_FORMAT,
} from '../data/constants';
import { DatePrecisions } from '../types';

const dateTimestampToStoredDateString = (
	dateTimestamp: string,
	precision: DatePrecisions,
): string => {
	const parsedDate = dayjs(Number(dateTimestamp));

	const formattedDate = parsedDate.format(
		DATE_PRECISION_BACKEND_FORMAT[precision],
	);

	return formattedDate;
};

const getZoneNameAbbreviation = (timezone: string) => {
	const zone = dayjs().tz(timezone);
	return zone.offsetName('short');
};

const storedPolicyholderDateToDateString = (dateString: string) => {
	const parsedDate = dayjs(dateString);

	if (!parsedDate.isValid()) {
		return '';
	}

	const formattedDate = parsedDate.format(DATE_PRECISION_INPUT_FORMAT['day']);

	return formattedDate;
};

const getDateOffset = (
	offset: DateOffsetConfiguration,
	todayOverwriteForTests?: dayjs.Dayjs,
): dayjs.Dayjs => {
	if (!offset) {
		return undefined;
	}
	const now = todayOverwriteForTests ? todayOverwriteForTests : dayjs();

	let offsetDateObject;

	if (offset.amount.includes('-')) {
		offsetDateObject = now.subtract(
			Math.abs(parseInt(offset.amount, 10)),
			offset.field,
		);
	} else {
		offsetDateObject = now.add(parseInt(offset.amount, 10), offset.field);
	}
	if (offset.roundToDay) {
		return offsetDateObject.startOf('day');
	}
	return offsetDateObject;
};

const getMostRecentOfTwoDates = (date1: dayjs.Dayjs, date2: dayjs.Dayjs) => {
	if ((!date1 || !date1.isValid()) && (!date2 || !date2.isValid())) {
		return undefined;
	}
	if ((!date1 || !date1.isValid()) && date2) {
		return date2;
	}
	if ((!date2 || !date2.isValid()) && date1) {
		return date1;
	}
	if (date1 > date2) {
		return date1;
	}
	return date2;
};

const getLatestOfTwoDates = (date1: dayjs.Dayjs, date2: dayjs.Dayjs) => {
	if ((!date1 || !date1.isValid()) && (!date2 || !date2.isValid())) {
		return undefined;
	}
	if ((!date1 || !date1.isValid()) && date2) {
		return date2;
	}
	if ((!date2 || !date2.isValid()) && date1) {
		return date1;
	}
	if (date1 < date2) {
		return date1;
	}
	return date2;
};

const getMaximumDateAllowedForDateField = (
	dateField: DateFieldConfiguration,
) => {
	const maxDate = dayjs(dateField.maximum, DATE_PRECISION_INPUT_FORMAT.day);
	return maxDate;
};

const getMinimumDateAllowedForDateField = (
	dateField: DateFieldConfiguration,
) => {
	const minDate = dayjs(dateField.minimum, DATE_PRECISION_INPUT_FORMAT.day);
	return minDate;
};

const getDateBoundaryForDateField = (
	dateField: DateFieldConfiguration,
	todayOverwriteForTests?: dayjs.Dayjs,
): {
	minDate?: dayjs.Dayjs;
	maxDate?: dayjs.Dayjs;
} => {
	let minDate = undefined as dayjs.Dayjs;
	let maxDate = undefined as dayjs.Dayjs;
	const minDateObj =
		dateConversion.getMinimumDateAllowedForDateField(dateField);

	const maxDateObj =
		dateConversion.getMaximumDateAllowedForDateField(dateField);

	const minOffsetObj = dateConversion.getDateOffset(
		dateField.minimumOffset,
		todayOverwriteForTests,
	);

	const maxOffsetObj = dateConversion.getDateOffset(
		dateField.maximumOffset,
		todayOverwriteForTests,
	);

	if (minOffsetObj) {
		minDate = minOffsetObj;
	}
	if (maxOffsetObj) {
		maxDate = maxOffsetObj;
	}

	if (minDateObj) {
		minDate = getMostRecentOfTwoDates(minDate, minDateObj);
	}
	if (maxDateObj) {
		maxDate = getLatestOfTwoDates(maxDate, maxDateObj);
	}

	return {
		minDate,
		maxDate,
	};
};
/**
 * Converts a timestamp from the API into a dayJS object in the configured timezone
 *
 * Example: '2022/01/01' will return a timestamp of '2022/01/01 00:00:00'
 * @param {string} date in DATE_PRECISION.DAY format (YYYY/MM/DD)
 * @param {string} timezone in IANA format (America/New_York)
 * @return {string} timestamp at midnight
 */
const convertDateStringToTimestamp = (date: string) => {
	if (date === '') {
		return '';
	}
	const dateObject = dateFormStringToDayJs(date, DATE_PRECISION.DAY);
	if (!dateObject.isValid()) {
		return '';
	}
	return '' + dateObject.valueOf();
};

/**
 * Converts a timestamp from the API into a dayJS object in the configured timezone
 *
 * Example: '2022/01/01' in 'America/New_York' will return a timestamp of '2022/01/01 00:00:00' in 'America/New_York'
 * @param {string} date in DATE_PRECISION.DAY format (YYYY/MM/DD)
 * @param {string} precision DatePrecisions of the date
 * @param {string} timezone in IANA format (America/New_York)
 * @return {string} timestamp in zone at midnight
 */
const convertDateStringToZonedTimestamp = (
	date: string,
	precision: DatePrecisions,
	timezone: string,
) => {
	if (date === '') {
		return '';
	}
	const dateObject = dateFormStringToDayJs(date, precision);
	if (!dateObject.isValid()) {
		return '';
	}

	// SOCA-10819 Fix for Dayjs Daylight Savings Bug - use luxon if timezone is GMT/UTC
	if (
		timezone == 'GMT' ||
		timezone == 'Etc/GMT' ||
		timezone == 'UTC' ||
		timezone == 'Etc/UTC'
	) {
		const luxonUnixTimestamp = DateTime.fromFormat(
			date,
			DATE_PRECISION_LUXON_FORMAT[precision],
			{
				zone: timezone,
			},
		)
			.setZone(timezone)
			.toMillis();
		return luxonUnixTimestamp.toString();
	}

	return '' + dateObject.tz(timezone, true).valueOf();
};

/**
 * Converts a timestamp from the API into a dayJS object in the configured timezone
 *
 * Examples:
 * A timestamp of '2022/01/01 00:00:00' in 'America/New_York' will return a dayJs object of '2022/01/01 00:00:00' in 'America/New_York'
 * A timestamp of '2022/01/01 00:00:00' in 'America/Los_Angeles' will return a dayJs object of '2022/01/01 02:00:00' in 'America/Chicago'
 * @param {string} timestamp in milliseconds
 * @param {string} timezone in IANA format (America/New_York)
 * @return {dayjs.Dayjs} dayJs object in the configured timezone
 */
const convertTimestampToZonedDayJS = (timestamp: string, timezone: string) => {
	return dayjs(parseInt(timestamp, 10)).tz(timezone, true);
};

/**
 * Converts a timestamp from the API into a dayJS object in the configured timezone without changing time
 *
 * Examples:
 * A timestamp of '2022/01/01 00:00:00' in 'America/New_York' will return a dayJs object of '2022/01/01 00:00:00' in 'America/New_York'
 * A timestamp of '2022/01/01 00:00:00' in 'America/Los_Angeles' will return a dayJs object of '2022/01/01 00:00:00' in 'America/Chicago'
 * @param {string} timestamp in milliseconds
 * @param {string} timezone in IANA format (America/New_York)
 * @return {dayjs.Dayjs} dayJs object in the configured timezone
 */
const convertTimestampToDayJS = (timestamp: string, timezone: string) => {
	return dayjs(parseInt(timestamp, 10)).tz(timezone);
};

/**
 * Converts a string from the API into a dayJS object
 * Strips off stored TZ offset and replaces it with the local TZ offset of the browser.
 * @param {string} dateString
 * @param {DatePrecisions} precision
 * @param {string} timezone
 * @return {*}  {(dayjs.Dayjs | '')}
 */
const backendFormattedStringToLocalDayJs = (
	dateString: string,
	precision: DatePrecisions,
): dayjs.Dayjs | '' => {
	// Check dateString is a string
	if (typeof dateString !== 'string') {
		return '';
	}
	// Get a day object from the date string so DST is taken into account
	const date = dayjs(dateString, DATE_PRECISION_BACKEND_FORMAT[precision]);

	if (precision === DATE_PRECISION.SECOND) {
		const localTZOffset = date.tz().format('ZZ');
		dateString = dateString.replace(
			/(Z|[+-][0-9]{2}(:?[0-9]{2})?)$/,
			localTZOffset,
		);
	}
	const parsedDate = dayjs(
		dateString,
		DATE_PRECISION_BACKEND_FORMAT[precision],
	);

	return parsedDate;
};

/**
 * Converts a string from the form library into API formatted string
 * If precision is type second, it will strip the local TZ offset and replace it with the configured TZ offset using regex.
 * @param {(dayjs.Dayjs | null)} date
 * @param {DatePrecisions} precision
 * @param {string} timezone
 * @return {*}  {string}
 */
const dayJsToLocalizedBackendFormattedString = (
	date: dayjs.Dayjs | null,
	precision: DatePrecisions,
	timezone: string,
): string => {
	if (date && date.isValid()) {
		let backendFormattedDateString = dayjs(date).format(
			DATE_PRECISION_BACKEND_FORMAT[precision],
		);

		if (precision === DATE_PRECISION.SECOND) {
			const configuredTZOffset = dayjs.tz(date, timezone).format('ZZ');
			backendFormattedDateString = backendFormattedDateString.replace(
				/(Z|[+-][0-9]{2}(:?[0-9]{2})?)$/,
				configuredTZOffset,
			);
		}
		return backendFormattedDateString;
	} else {
		return '';
	}
};
/**
 * Converts a DayJs object from Material UIs datepicker library and converts to a string to be stored in the form library
 * Used by client only, does not adjust timezones.
 * @param {(dayjs.Dayjs | null)} date
 * @param {DatePrecisions} precision
 * @return {*}  {string}
 */
const dayJsObjectToDateFormString = (
	date: dayjs.Dayjs | null,
	precision: DatePrecisions,
): string => {
	if (date) {
		const inputFormattedDate = date.format(
			DATE_PRECISION_INPUT_FORMAT[precision],
		);
		return inputFormattedDate;
	} else {
		return '';
	}
};
/**
 * Converts a string from the form library into something that Material UI's Datepickers can use
 * Used by client only, does not adjust timezones.
 * @param {string} date
 * @param {DatePrecisions} precision
 * @return {*}  {dayjs.Dayjs}
 */
const dateFormStringToDayJs = (
	date: string,
	precision: DatePrecisions,
): dayjs.Dayjs => {
	const dateObject = dayjs(date, DATE_PRECISION_INPUT_FORMAT[precision]);
	return dateObject;
};

const isDateInAffectedRange = (
	dateString: string,
	startDateString: string,
	endDateString: string,
) => {
	// Define the start and end date for the range
	const startDate = dayjs(startDateString);
	const endDate = dayjs(endDateString);

	// Parse the input date string using dayjs
	const inputDate = dayjs(dateString, ['YYYY/MM/DD HH:mm:ss a', 'YYYY/MM/DD']);

	// Check if the date is within the specified range
	return inputDate.isAfter(startDate) && inputDate.isBefore(endDate);
};

export const dateConversion = {
	getDateOffset,
	getDateBoundaryForDateField,
	getMostRecentOfTwoDates,
	getLatestOfTwoDates,
	dateFormStringToDayJs,
	backendFormattedStringToLocalDayJs,
	dayJsObjectToDateFormString,
	dayJsToLocalizedBackendFormattedString,
	getZoneNameAbbreviation,
	storedPolicyholderDateToDateString,
	getMinimumDateAllowedForDateField,
	getMaximumDateAllowedForDateField,
	dateTimestampToStoredDateString,
	convertDateStringToTimestamp,
	convertDateStringToZonedTimestamp,
	convertTimestampToZonedDayJS,
	convertTimestampToDayJS,
	isDateInAffectedRange,
};
